import { Instagram, Linkedin, Twitter, Mail } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useConfig } from "@/context/ConfigContext";
import { useDemo } from "@/context/DemoContext";
import { scrollToId } from "@/hooks/useLenis";

const COLS = [
  {
    title: "Products",
    links: [
      { label: "Crash Ready", id: "platform" },
      { label: "Ecosystem", id: "ecosystem" },
      { label: "Platform Flow", id: "flow" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", id: "company" },
      { label: "Vision", id: "vision" },
      { label: "Why Cogentis", id: "ecosystem" },
    ],
  },
];

export function Footer() {
  const { config } = useConfig();
  const { launchDemo } = useDemo();
  const year = new Date().getFullYear();
  const email = config?.company_email;
  const instagram = config?.instagram;
  const igHandle = instagram ? instagram.replace(/^@/, "") : "";

  return (
    <footer
      id="footer"
      className="relative border-t border-white/10 bg-[#050507]"
      data-testid="footer"
    >
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo height={34} showTagline />
            <p className="font-body mt-6 max-w-sm text-sm leading-relaxed text-white/45">
              Engineering intelligent software that transforms complex
              information into confident decisions.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {email && (
                <a
                  href={`mailto:${email}`}
                  data-testid="footer-email-link"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-white/30 hover:text-white"
                  aria-label="Email"
                >
                  <Mail className="h-4 w-4" />
                </a>
              )}
              {igHandle && (
                <a
                  href={`https://instagram.com/${igHandle}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram-link"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-white/30 hover:text-white"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {config?.linkedin && (
                <a
                  href={config.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-linkedin-link"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-white/30 hover:text-white"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              )}
              {config?.twitter && (
                <a
                  href={config.twitter}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-twitter-link"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-white/30 hover:text-white"
                  aria-label="X"
                >
                  <Twitter className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <p className="font-mono text-xs tracking-[0.2em] text-white/40">
                {col.title.toUpperCase()}
              </p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => scrollToId(l.id)}
                      className="font-body text-sm text-white/60 transition-colors hover:text-white"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-3">
            <p className="font-mono text-xs tracking-[0.2em] text-white/40">
              CONTACT
            </p>
            <ul className="mt-5 space-y-3 font-body text-sm text-white/60">
              {email && (
                <li>
                  <a href={`mailto:${email}`} className="hover:text-white">
                    {email}
                  </a>
                </li>
              )}
              {config?.phone && <li>{config.phone}</li>}
              {config?.address && <li className="text-white/45">{config.address}</li>}
              {instagram && <li>Instagram: {instagram}</li>}
            </ul>
            <button
              onClick={launchDemo}
              data-testid="footer-launch-demo-btn"
              className="font-body mt-6 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white transition-colors hover:border-white/40 hover:bg-white/5"
            >
              Launch Demo
            </button>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-sm text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="font-body">
              © {year} Cogentis Technologies. All rights reserved.
            </span>
            <button className="font-body hover:text-white/70">Privacy</button>
            <button className="font-body hover:text-white/70">Terms</button>
          </div>
          <p className="font-body text-white/35">
            Crash Ready™ is a product developed by Cogentis Technologies.
          </p>
        </div>
      </div>
    </footer>
  );
}
